import { z } from 'zod';
/** Typed application settings stored in the settings table, keyed by name. */
export const settingsSchema = z.object({
    'company.name': z.string().default('Borg & Bigum A/S'),
    'company.locale': z.enum(['da', 'en']).default('da'),
    'company.timezone': z.string().default('Europe/Copenhagen'),
    'printing.agentOfflineAfterSeconds': z.number().int().min(60).default(180),
    'printing.jobLeaseSeconds': z.number().int().min(10).default(60),
    'printing.maxAttempts': z.number().int().min(1).default(3),
    'security.sessionHours': z.number().int().min(1).default(12),
    'security.requireTotp': z.boolean().default(false),
    // 3x order feed (docs/design/02-3x-order-feed.md)
    'x3.enabled': z.boolean().default(false),
    'x3.baseUrl': z.string().default('https://shops.borgbigum.com/api/gaveuniverset'),
    /** Secret: stored encrypted, returned masked. */
    'x3.token': z.string().default(''),
    'x3.testMode': z.boolean().default(false),
    /** Secret: 3x's Gaveuniverset API token (`x-bb-token`), which guards the shop API. */
    'x3.shopToken': z.string().default(''),
    /** Refresh every mirrored shop from 3x at this hour (Europe/Copenhagen). */
    'x3.shopSyncHour': z.number().int().min(0).max(23).default(5),
    'x3.pollMinutes': z.number().int().min(1).max(60).default(2),
    /** Only orders created on or after this date are imported (current year rule). */
    'x3.ordersSince': z.string().regex(/^\d{4}-\d{2}-\d{2}$/).default(`${new Date().getUTCFullYear()}-01-01`),
    /** Shops are mirrored only when active or created within this many days. */
    'x3.shopActiveWithinDays': z.number().int().min(1).default(180),
    /**
     * History is fetched by order-number ranges (3x cannot serve a whole year in one call).
     * The backfill walks from the first order of `x3.ordersSince` to the newest order, 50 at a time.
     */
    'x3.backfillEnabled': z.boolean().default(true),
    'x3.backfillStart': z.number().int().nullable().default(null),
    'x3.backfillCursor': z.number().int().nullable().default(null),
    'x3.backfillEnd': z.number().int().nullable().default(null),
    'x3.backfillDone': z.boolean().default(false),
    /** A known existing 3x order number used as the starting point for probing. */
    'x3.backfillProbeFrom': z.number().int().default(900000),
    // Navision through the YouWe bridge (stock_inventory, outstanding_sales, outstanding_purchases)
    'nav.enabled': z.boolean().default(false),
    /** `x3`: read the bridge through 3x's order API (`navision/{endpoint}`, 3x token); `bridge`: call the bridge directly with the secret key. */
    'nav.mode': z.enum(['sql', 'x3', 'bridge']).default('sql'),
    'nav.baseUrl': z.string().default('https://borgbigumbridge.youweprint.dk'),
    /** Secret: the bridge's Secret-Key header. */
    'nav.secretKey': z.string().default(''),
    /** Hour of day (Europe/Copenhagen) for the nightly sync. */
    'nav.syncHour': z.number().int().min(0).max(23).default(4),
    // Mode `sql`: Navision's SQL Server directly (through the BB VPN), the same queries the YouWe bridge ran.
    'nav.sql.host': z.string().default('10.100.100.45'),
    'nav.sql.port': z.number().int().min(1).max(65535).default(1433),
    'nav.sql.database': z.string().default('BBNAV'),
    /** Table prefix of the NAV company, e.g. `Borg & Bigum Danmark A_S$`. */
    'nav.sql.company': z.string().default('Borg & Bigum Danmark A_S$'),
    'nav.sql.domain': z.string().default('B-B'),
    'nav.sql.user': z.string().default(''),
    /** Secret. */
    'nav.sql.password': z.string().default(''),
    /** Minutes between reads of Navision's sales orders, shipments and invoices for our 3x orders (mode sql). */
    'nav.orderSyncMinutes': z.number().int().min(5).max(1440).default(60),
    /** Close our orders that Navision has shipped or invoiced (parallel run): shipped -> shipped, invoiced -> completed. */
    'nav.applyOrderStatus': z.boolean().default(true),
    /** NAV location code -> our warehouse code, for codes that differ; empty means same codes. */
    'nav.locationMap': z.record(z.string(), z.string()).default({}),
    /** Shipping: the company on customs invoices and as initiator towards carriers. */
    'shipping.companyName': z.string().default('Borg & Bigum Danmark A/S'),
    'shipping.vatNumber': z.string().default(''),
    'shipping.eori': z.string().default(''),
    /** Incoterm on customs invoices; DAP = we pay freight, the receiver pays duties. */
    'shipping.incoterm': z.string().default('DAP'),
    /** Hours between tracking polls of booked shipments. */
    'shipping.trackHours': z.number().int().min(1).max(24).default(1),
    /** Capture the card payment (Bambora) when an order is fully shipped. Gateways in dry-run only record it. */
    'payments.captureOnShip': z.boolean().default(true),
    /** Outgoing mail over SMTP; without a host, mails stay in the outbox as "not sent". */
    'mail.enabled': z.boolean().default(false),
    'mail.smtpHost': z.string().default(''),
    'mail.smtpPort': z.number().int().min(1).max(65535).default(587),
    'mail.smtpSecure': z.boolean().default(false),
    'mail.smtpUser': z.string().default(''),
    'mail.smtpPassword': z.string().default(''),
    'mail.from': z.string().default('Borg & Bigum <noreply@b-b.dk>'),
    'mail.replyTo': z.string().default(''),
    /** Send the customer a mail with track and trace when their order ships. Off during the parallel run. */
    'mail.trackingEmail': z.boolean().default(false),
    /** Every mail goes to this address instead of the customer (testing); empty = real recipients. */
    'mail.redirectTo': z.string().default(''),
    /** P2.5: days after a hand-out it can be taken back; 0 means no limit (QR2's default). */
    'handout.recallDays': z.number().int().min(0).max(365).default(14),
    /** Gaveuniverset (P3.4): warn when fewer free physical gift cards than this are left, by mail to this address. */
    'gu.cardLowStock': z.number().int().min(0).max(100000).default(200),
    'gu.cardAlertEmail': z.string().default(''),
    /** P3.6: the daily Gaveuniverset report (new orders, shops closing, stock) goes here; empty = no report. */
    'gu.reportEmail': z.string().default(''),
    /** Finance (docs/design/05): Dinero through Visma Connect. Off and in dry-run until the organisation exists. */
    'finance.enabled': z.boolean().default(false),
    'finance.mode': z.enum(['dry_run', 'live']).default('dry_run'),
    /** Orders shipped on or after this day are invoiced from Dinero; earlier ones stay with Navision. */
    'finance.startDate': z.string().default(''),
    /** Every invoice waits in the work list until someone approves it (first week after cut-over). */
    'finance.manualApproval': z.boolean().default(true),
    /** 3x's prices include VAT; Dinero then works the VAT out per account. */
    'finance.pricesInclVat': z.boolean().default(true),
    /** Mail the invoice to card-paying customers as a receipt (otherwise it is only the sales voucher). */
    'finance.cardReceipts': z.boolean().default(false),
    'finance.defaultPaymentDays': z.number().int().min(0).max(120).default(14),
    'finance.accountMap': z.record(z.string(), z.number().int()).default({}),
    'finance.invoiceTemplateId': z.string().default(''),
    'finance.dinero.clientId': z.string().default(''),
    'finance.dinero.clientSecret': z.string().default(''),
    'finance.dinero.organizationId': z.string().default(''),
    'finance.dinero.refreshToken': z.string().default(''),
    /** Empty = Dinero; the local mock in development. */
    'finance.dinero.baseUrl': z.string().default(''),
});
export const SETTING_KEYS = Object.keys(settingsSchema.shape);
/** Keys whose values are encrypted at rest and masked in API responses. */
export const SECRET_SETTING_KEYS = ['x3.token', 'x3.shopToken', 'nav.secretKey', 'nav.sql.password', 'mail.smtpPassword', 'finance.dinero.clientSecret', 'finance.dinero.refreshToken'];
export const SECRET_MASK = '••••••••';
/**
 * Partial update: every key optional and WITHOUT its default, so a patch only touches the keys it names.
 * (`settingsSchema.partial()` would fill absent keys with their defaults and reset the rest.)
 */
export const updateSettingsRequest = z.object(Object.fromEntries(Object.entries(settingsSchema.shape).map(([k, v]) => [k, (v instanceof z.ZodDefault ? v.removeDefault() : v).optional()])));
//# sourceMappingURL=settings.js.map